import { useEffect, useState, useCallback } from "react";
import { auth, db } from "../../context/Firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import "../../style/ServiceDashboard.css";
import "./Feedback.css";

const Feedback = () => {
  const [ratings, setRatings] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest"); // newest, oldest, highest, lowest
  const [stats, setStats] = useState({
    totalFeedback: 0,
    averageRating: 0,
    fiveStars: 0,
    fourStars: 0,
    threeStars: 0,
    twoStars: 0,
    oneStar: 0,
    withComments: 0,
  });

  // Fetch services and categories for mapping
  const fetchServicesAndCategories = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      console.log("🔄 Fetching services and categories for mapping...");

      // Fetch services
      const servicesQuery = query(
        collection(db, "service_services"),
        where("companyId", "==", user.uid)
      );
      const servicesSnap = await getDocs(servicesQuery);
      const servicesList = servicesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch categories
      const categoriesQuery = query(
        collection(db, "service_categories"),
        where("companyId", "==", user.uid)
      );
      const categoriesSnap = await getDocs(categoriesQuery);
      const categoriesList = categoriesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`📊 Fetched ${servicesList.length} services and ${categoriesList.length} categories`);
      setServices(servicesList);
      setCategories(categoriesList);

    } catch (error) {
      console.error("❌ Error fetching services and categories:", error);
    }
  }, []);

  // Map service and category names to feedback
  const enrichFeedbackData = useCallback((feedbackList) => {
    return feedbackList.map(feedback => {
      console.log("🔍 Processing feedback:", feedback);
      
      // Find service name - try multiple field names
      let serviceName = feedback.serviceName || feedback.service || feedback.serviceTitle || 'Unknown Service';
      let categoryName = feedback.categoryName || feedback.category || feedback.categoryTitle || null;

      // Try to find service by ID if serviceName is not available or is generic
      if (feedback.serviceId && services.length > 0) {
        const service = services.find(s => s.id === feedback.serviceId);
        if (service) {
          serviceName = service.name || service.title || serviceName;
          console.log(`✅ Found service by ID: ${serviceName}`);
          
          // Also get category from service
          if (service.categoryId && !categoryName) {
            const category = categories.find(c => c.id === service.categoryId);
            if (category) {
              categoryName = category.name || category.title;
              console.log(`✅ Found category from service: ${categoryName}`);
            }
          }
        }
      }

      // Try to find category by ID if categoryName is not available
      if (feedback.categoryId && !categoryName && categories.length > 0) {
        const category = categories.find(c => c.id === feedback.categoryId);
        if (category) {
          categoryName = category.name || category.title;
          console.log(`✅ Found category by ID: ${categoryName}`);
        }
      }

      // If still no service name, try to get it from category context
      if (serviceName === 'Unknown Service' && categoryName) {
        serviceName = `${categoryName} Service`;
      }

      console.log(`📝 Final mapping - Service: ${serviceName}, Category: ${categoryName}`);

      return {
        ...feedback,
        serviceName,
        categoryName
      };
    });
  }, [services, categories]);

  const setupRealtimeFeedbackListener = useCallback(() => {
    const user = auth.currentUser;
    if (!user) {
      console.log("❌ No authenticated user found");
      setLoading(false);
      return;
    }

    console.log("🔄 Setting up real-time feedback listener for user:", user.uid);

    // Try multiple collection names and field combinations
    const collectionNames = ["serviceRatings", "service_ratings", "ratings", "feedback"];
    const fieldNames = ["companyId", "serviceCompanyId", "company", "providerId"];
    
    let unsubscribe = null;
    let listenerActive = false;

    const tryListener = async (collectionName, fieldName) => {
      try {
        console.log(`🔍 Trying listener: ${collectionName}.${fieldName} == ${user.uid}`);
        
        // Simple query without orderBy to avoid index requirement
        const q = query(
          collection(db, collectionName),
          where(fieldName, "==", user.uid)
        );

        return onSnapshot(q, (snapshot) => {
          console.log(`📊 ${collectionName} snapshot received:`, {
            size: snapshot.size,
            empty: snapshot.empty,
            changes: snapshot.docChanges().length
          });

          if (!snapshot.empty || listenerActive) {
            listenerActive = true;
            
            const feedbackList = [];
            snapshot.docs.forEach(doc => {
              const data = doc.data();
              console.log("📝 Feedback document:", doc.id, data);
              
              // Handle different timestamp formats
              let createdAt = new Date();
              if (data.createdAt && typeof data.createdAt.toDate === 'function') {
                createdAt = data.createdAt.toDate();
              } else if (data.timestamp) {
                createdAt = new Date(data.timestamp);
              } else if (data.createdAt) {
                createdAt = new Date(data.createdAt);
              }
              
              feedbackList.push({
                id: doc.id,
                ...data,
                createdAt: createdAt,
              });
            });

            // Sort manually by date (newest first)
            feedbackList.sort((a, b) => b.createdAt - a.createdAt);

            // Enrich with service and category names
            const enrichedFeedback = enrichFeedbackData(feedbackList);

            console.log(`✅ Real-time feedback updated: ${enrichedFeedback.length} items`);
            setRatings(enrichedFeedback);
            calculateStats(enrichedFeedback);
            setLoading(false);
          }
        }, (error) => {
          console.error(`❌ Listener error for ${collectionName}.${fieldName}:`, error);
          throw error;
        });

      } catch (error) {
        console.error(`❌ Failed to setup listener for ${collectionName}.${fieldName}:`, error);
        return null;
      }
    };

    // Try different combinations
    const setupListener = async () => {
      for (const collectionName of collectionNames) {
        for (const fieldName of fieldNames) {
          try {
            const listener = await tryListener(collectionName, fieldName);
            if (listener) {
              unsubscribe = listener;
              console.log(`✅ Successfully setup listener: ${collectionName}.${fieldName}`);
              return;
            }
          } catch (error) {
            console.log(`⚠️ Listener failed: ${collectionName}.${fieldName}`);
            continue;
          }
        }
      }

      // If no specific listeners worked, try to get all documents as fallback
      console.log("🔄 Trying fallback: fetch all documents and filter...");
      try {
        const allListener = onSnapshot(collection(db, "serviceRatings"), (snapshot) => {
          console.log(`📊 All documents snapshot: ${snapshot.size} total`);
          
          const allDocs = [];
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            
            // Handle different timestamp formats
            let createdAt = new Date();
            if (data.createdAt && typeof data.createdAt.toDate === 'function') {
              createdAt = data.createdAt.toDate();
            } else if (data.timestamp) {
              createdAt = new Date(data.timestamp);
            } else if (data.createdAt) {
              createdAt = new Date(data.createdAt);
            }
            
            allDocs.push({
              id: doc.id,
              ...data,
              createdAt: createdAt,
            });
          });

          // Filter for this company
          const feedbackList = allDocs.filter(doc => 
            doc.companyId === user.uid || 
            doc.serviceCompanyId === user.uid || 
            doc.company === user.uid ||
            doc.providerId === user.uid
          );

          // Sort manually by date (newest first)
          feedbackList.sort((a, b) => b.createdAt - a.createdAt);

          // Enrich with service and category names
          const enrichedFeedback = enrichFeedbackData(feedbackList);

          console.log(`✅ Fallback filter result: ${enrichedFeedback.length} items for company`);
          setRatings(enrichedFeedback);
          calculateStats(enrichedFeedback);
          setLoading(false);
        }, (error) => {
          console.error("❌ Fallback listener error:", error);
          // If even fallback fails, just show empty state
          setRatings([]);
          calculateStats([]);
          setLoading(false);
        });

        unsubscribe = allListener;
      } catch (error) {
        console.error("❌ Fallback listener setup failed:", error);
        setRatings([]);
        calculateStats([]);
        setLoading(false);
      }
    };

    setupListener();

    // Return cleanup function
    return () => {
      if (unsubscribe) {
        console.log("🧹 Cleaning up feedback listener");
        unsubscribe();
      }
    };
  }, [enrichFeedbackData]);

  // Manual refresh function
  const handleRefresh = () => {
    console.log("🔄 Manual refresh triggered");
    setLoading(true);
    
    // Re-fetch services and categories, then re-setup the listener
    fetchServicesAndCategories().then(() => {
      const cleanup = setupRealtimeFeedbackListener();
      
      // Set a timeout to stop loading if no data comes
      setTimeout(() => {
        if (loading) {
          console.log("⏰ Refresh timeout, stopping loading");
          setLoading(false);
        }
      }, 10000); // 10 second timeout
      
      return cleanup;
    });
  };

  const calculateStats = (feedbackList) => {
    const stats = feedbackList.reduce((acc, feedback) => {
      const rating = parseInt(feedback.rating || 0);
      
      acc.totalFeedback++;
      acc.averageRating += rating;

      // Rating distribution
      switch (rating) {
        case 5: acc.fiveStars++; break;
        case 4: acc.fourStars++; break;
        case 3: acc.threeStars++; break;
        case 2: acc.twoStars++; break;
        case 1: acc.oneStar++; break;
        default: break;
      }

      // Comments count
      if (feedback.feedback && feedback.feedback.trim()) {
        acc.withComments++;
      }

      return acc;
    }, {
      totalFeedback: 0,
      averageRating: 0,
      fiveStars: 0,
      fourStars: 0,
      threeStars: 0,
      twoStars: 0,
      oneStar: 0,
      withComments: 0,
    });

    // Calculate average
    if (stats.totalFeedback > 0) {
      stats.averageRating = (stats.averageRating / stats.totalFeedback).toFixed(1);
    }

    setStats(stats);
  };

  useEffect(() => {
    console.log("🚀 Feedback component mounted, fetching data...");
    
    // First fetch services and categories, then setup listener
    fetchServicesAndCategories().then(() => {
      const cleanup = setupRealtimeFeedbackListener();
      return cleanup;
    });
  }, [fetchServicesAndCategories, setupRealtimeFeedbackListener]);

  // Filter and sort feedback
  const getFilteredAndSortedRatings = () => {
    let filtered = ratings.filter((rating) => {
      const matchesSearch = !searchQuery || 
        rating.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rating.feedback?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rating.serviceName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rating.categoryName?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesRating = ratingFilter === "all" || 
        parseInt(rating.rating) === parseInt(ratingFilter);

      return matchesSearch && matchesRating;
    });

    // Sort based on selected option
    switch (sortBy) {
      case "newest":
        filtered.sort((a, b) => b.createdAt - a.createdAt);
        break;
      case "oldest":
        filtered.sort((a, b) => a.createdAt - b.createdAt);
        break;
      case "highest":
        filtered.sort((a, b) => parseInt(b.rating || 0) - parseInt(a.rating || 0));
        break;
      case "lowest":
        filtered.sort((a, b) => parseInt(a.rating || 0) - parseInt(b.rating || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredRatings = getFilteredAndSortedRatings();

  const renderStars = (rating) => {
    const stars = [];
    const numRating = parseInt(rating || 0);
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <svg
          key={i}
          className={`feedback-star ${i <= numRating ? 'filled' : 'empty'}`}
          viewBox="0 0 24 24"
          fill={i <= numRating ? "currentColor" : "none"}
          stroke="currentColor"
        >
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
        </svg>
      );
    }
    return stars;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRatingColor = (rating) => {
    const numRating = parseInt(rating || 0);
    if (numRating >= 4) return 'feedback-rating-good';
    if (numRating >= 3) return 'feedback-rating-average';
    return 'feedback-rating-poor';
  };

  const getRatingPercentage = (count) => {
    return stats.totalFeedback > 0 ? ((count / stats.totalFeedback) * 100).toFixed(1) : 0;
  };

  if (loading) {
    return (
      <div className="sd-main">
        <div className="feedback-loading">
          <div className="feedback-loading-spinner"></div>
          <p>Loading feedback...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sd-main feedback-page">
      {/* Page Header */}
      <div className="feedback-page-header">
        <div className="feedback-header-content">
          <h1>⭐ Customer Feedback & Ratings</h1>
          <p>View and analyze customer ratings and feedback for your services</p>
          <div className="feedback-live-indicator">
            <div className="feedback-live-dot"></div>
            <span>Live Data</span>
          </div>
        </div>
        <button 
          className="feedback-refresh-btn"
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="feedback-btn-spinner"></div>
              Refreshing...
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
              Refresh
            </>
          )}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="feedback-stats-grid">
        <div className="feedback-stat-card average">
          <div className="feedback-stat-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
            </svg>
          </div>
          <div className="feedback-stat-content">
            <p className="feedback-stat-label">Average Rating</p>
            <p className="feedback-stat-value">{stats.averageRating} <span className="feedback-stat-max">/ 5.0</span></p>
            <div className="feedback-stat-stars">
              {renderStars(Math.round(stats.averageRating))}
            </div>
          </div>
        </div>

        <div className="feedback-stat-card total">
          <div className="feedback-stat-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div className="feedback-stat-content">
            <p className="feedback-stat-label">Total Reviews</p>
            <p className="feedback-stat-value">{stats.totalFeedback}</p>
            <p className="feedback-stat-subtitle">All time feedback</p>
          </div>
        </div>

        <div className="feedback-stat-card positive">
          <div className="feedback-stat-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z"/>
            </svg>
          </div>
          <div className="feedback-stat-content">
            <p className="feedback-stat-label">With Comments</p>
            <p className="feedback-stat-value">{stats.withComments}</p>
            <p className="feedback-stat-subtitle">{getRatingPercentage(stats.withComments)}% of reviews</p>
          </div>
        </div>

        <div className="feedback-stat-card excellent">
          <div className="feedback-stat-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className="feedback-stat-content">
            <p className="feedback-stat-label">5-Star Reviews</p>
            <p className="feedback-stat-value">{stats.fiveStars}</p>
            <p className="feedback-stat-subtitle">{getRatingPercentage(stats.fiveStars)}% excellent</p>
          </div>
        </div>
      </div>

      {/* Rating Distribution */}
      <div className="feedback-distribution-card">
        <div className="feedback-distribution-header">
          <h3>📊 Rating Distribution</h3>
          <p>Breakdown of customer ratings</p>
        </div>
        <div className="feedback-distribution-content">
          {[5, 4, 3, 2, 1].map(star => {
            const count = stats[`${['', 'one', 'two', 'three', 'four', 'five'][star]}Star${star === 1 ? '' : 's'}`] || 0;
            const percentage = getRatingPercentage(count);
            
            return (
              <div key={star} className="feedback-distribution-row">
                <div className="feedback-distribution-label">
                  <span className="feedback-distribution-star-count">{star}</span>
                  <svg className="feedback-distribution-star-icon" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                  </svg>
                </div>
                <div className="feedback-distribution-bar-container">
                  <div 
                    className={`feedback-distribution-bar star-${star}`}
                    style={{ width: `${percentage}%` }}
                  >
                    <span className="feedback-distribution-bar-label">{percentage}%</span>
                  </div>
                </div>
                <span className="feedback-distribution-count">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="feedback-filters">
        <div className="feedback-search-wrapper">
          <svg className="feedback-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search by service, category, or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="feedback-search-input"
          />
          {searchQuery && (
            <button 
              className="feedback-search-clear"
              onClick={() => setSearchQuery("")}
            >
              ✕
            </button>
          )}
        </div>

        <div className="feedback-filter-group">
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className="feedback-filter-select"
          >
            <option value="all">All Ratings</option>
            <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
            <option value="4">⭐⭐⭐⭐ 4 Stars</option>
            <option value="3">⭐⭐⭐ 3 Stars</option>
            <option value="2">⭐⭐ 2 Stars</option>
            <option value="1">⭐ 1 Star</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="feedback-filter-select"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
        </div>
      </div>

      {/* Results Count */}
      {(searchQuery || ratingFilter !== "all") && (
        <div className="feedback-results-info">
          <p>
            Showing <strong>{filteredRatings.length}</strong> of <strong>{ratings.length}</strong> reviews
            {searchQuery && ` matching "${searchQuery}"`}
            {ratingFilter !== "all" && ` with ${ratingFilter} stars`}
          </p>
          {(searchQuery || ratingFilter !== "all") && (
            <button 
              className="feedback-clear-filters"
              onClick={() => {
                setSearchQuery("");
                setRatingFilter("all");
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Feedback List */}
      <div className="feedback-list">
        {filteredRatings.length === 0 ? (
          <div className="feedback-empty-state">
            <div className="feedback-empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
              </svg>
            </div>
            <h3>No ratings found</h3>
            <p>
              {searchQuery || ratingFilter !== "all"
                ? "Try adjusting your search or filters"
                : "Customer ratings and feedback will appear here once customers rate your services"}
            </p>
            {(searchQuery || ratingFilter !== "all") && (
              <button 
                className="feedback-empty-clear-btn"
                onClick={() => {
                  setSearchQuery("");
                  setRatingFilter("all");
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          filteredRatings.map((rating) => (
            <div key={rating.id} className="modern-feedback-card">
              <div className="modern-feedback-header">
                <div className="modern-feedback-customer">
                  <div className="modern-feedback-avatar">
                    {(rating.customerName || 'A').charAt(0).toUpperCase()}
                  </div>
                  <div className="modern-feedback-customer-info">
                    <h4 className="modern-feedback-customer-name">{rating.customerName || 'Anonymous'}</h4>
                    <p className="modern-feedback-service-name">
                      {rating.serviceName || 'Service'}
                      {rating.categoryName && ` • ${rating.categoryName}`}
                    </p>
                    <span className="modern-feedback-date">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      {formatDate(rating.createdAt)}
                    </span>
                  </div>
                </div>
                <div className="modern-feedback-rating-section">
                  <div className={`modern-feedback-rating-badge ${getRatingColor(rating.rating)}`}>
                    <span className="modern-feedback-rating-number">{rating.rating || 0}</span>
                    <svg className="modern-feedback-star-icon" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                    </svg>
                  </div>
                  <div className="modern-feedback-stars">
                    {renderStars(rating.rating)}
                  </div>
                </div>
              </div>

              {rating.feedback && (
                <div className="modern-feedback-comment">
                  <div className="modern-feedback-quote-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z"/>
                    </svg>
                  </div>
                  <p className="modern-feedback-comment-text">"{rating.feedback}"</p>
                </div>
              )}

              <div className="modern-feedback-footer">
                <div className="modern-feedback-service-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                  {rating.serviceName || 'Service'}
                </div>
                {rating.categoryName && (
                  <div className="modern-feedback-category-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    {rating.categoryName}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Feedback;
